import Image from 'next/image';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { useRouter } from 'next/router';

import { DEFAULT_BLUR_IMAGE } from '../../../constants/blurImage';
import { useSessionPopup } from '../../../hooks/useSessionPopup';

type ImagePopupProps = {
  alt?: string;
  delayTimer?: number;
  href?: string;
  src: string;
  storageKey: string;
};

export function ImagePopup(props: ImagePopupProps) {
  const {
    alt = 'popup image',
    delayTimer = 3000,
    href,
    src,
    storageKey
  } = props;

  const router = useRouter();
  const { isModalOpen, onCloseHandler } = useSessionPopup(
    storageKey,
    delayTimer
  );

  function onImageClickHandler() {
    if (!href) return;
    onCloseHandler();
    router.push(href);
  }

  return (
    <Transition.Root show={isModalOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCloseHandler}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed z-50 inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel
              className="relative w-full shadow-xl transform transition-all"
              style={{ maxWidth: 'min(90vw, 80vh, 600px)' }}
            >
              <button
                aria-label="Close popup"
                className="absolute top-3 right-3 z-20 p-1 bg-white/80 hover:bg-white rounded-full"
                type="button"
                onClick={onCloseHandler}
              >
                <AiOutlineClose className="w-6 h-6 text-black" />
              </button>
              <div
                className={`relative w-full aspect-square bg-black ${
                  href ? 'cursor-pointer' : ''
                }`}
                onClick={onImageClickHandler}
              >
                <Image
                  alt={alt}
                  blurDataURL={DEFAULT_BLUR_IMAGE}
                  layout="fill"
                  objectFit="cover"
                  placeholder="blur"
                  src={src}
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default ImagePopup;
